import { Mutation } from '@varo/graphql-schema-types';
import {
  isGenerateAgreementsForAutoPayError,
  isGenerateAgreementsForAutoPaySuccess,
} from 'features/advance/helpers/AdvanceAgreements/AdvanceAgreements.helpers';
import { useEffect, useRef, useState } from 'react';
import GenerateAgreementsForAutopay from 'services/graphql/mutations/Advance/GenerateAgreementsForAutopay';
import useCancellableMutation, {
  AbortError,
} from 'shared/hooks/useCancellableMutation';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';

interface useAutoPaymentAuthorizationProps {
  paymentSource: string | undefined;
  totalRepaymentAmountWithFee: string | undefined;
  selectedAccountId: string | undefined;
  autoPaymentDate: string | undefined;
}

const useAutoPaymentAuthorization = ({
  paymentSource,
  totalRepaymentAmountWithFee,
  selectedAccountId,
  autoPaymentDate,
}: useAutoPaymentAuthorizationProps) => {
  const executedFetch = useRef(false);
  const [refetchError, setRefetchError] = useState(false);

  const [{ data, fetching, error }, refetch] = useCancellableMutation<Mutation>(
    GenerateAgreementsForAutopay,
  );

  const hasRequiredPayload = Boolean(
    paymentSource && selectedAccountId && autoPaymentDate,
  );

  useEffect(() => {
    if (hasRequiredPayload && !executedFetch.current) {
      executedFetch.current = true;

      const generateAutoPaymentAgreements = async () => {
        try {
          await refetch({
            input: {
              amount: {
                amount: totalRepaymentAmountWithFee,

                currency: 'USD',
              },

              autoPaymentDetails: {
                paymentSource,

                paymentAccountId: selectedAccountId,

                autoPaymentDate,
              },
            },
          });
        } catch (error) {
          if (error instanceof AbortError) {
            return false;
          }
          setRefetchError(true);
          logErrorWithContext(
            error instanceof Error
              ? error
              : new Error(
                  'Unexpected Advance AutoPayment Authorization  generating agreements error',
                ),
            {
              featureArea: FeatureArea.LENDING,
              responsibleTeam: ResponsibleTeam.LENDING_CREDIT,
              severity: ErrorSeverity.HIGH,
              additionalContext: {
                source: 'useAutoPaymentAuthorization',
                reason: 'Unexpected error during mutation',
              },
            },
          );

          return false;
        }
      };

      generateAutoPaymentAgreements();
    }
  }, [
    autoPaymentDate,
    hasRequiredPayload,
    paymentSource,
    refetch,
    selectedAccountId,
    totalRepaymentAmountWithFee,
  ]);

  const agreementsForAutoPaySuccess = isGenerateAgreementsForAutoPaySuccess(
    data?.generateAgreementsForAutopay,
  )
    ? data.generateAgreementsForAutopay?.agreements
    : undefined;

  const agreementsForAutoPayError = isGenerateAgreementsForAutoPayError(
    data?.generateAgreementsForAutopay,
  )
    ? data?.generateAgreementsForAutopay
    : undefined;

  const isError = Boolean(error || agreementsForAutoPayError || refetchError);

  return {
    data: agreementsForAutoPaySuccess,
    fetching,
    error: isError,
  };
};

export default useAutoPaymentAuthorization;
